import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Info, ExternalLink, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";

interface AboutDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const AboutDialog = ({ open, onOpenChange }: AboutDialogProps) => {
    const { t } = useTranslation();
    const version = "0.1.8";

    return (
        <Dialog onOpenChange={onOpenChange} open={open}>
            <DialogContent className="max-h-[80vh] max-w-sm md:max-w-lg overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Info className="h-5 w-5" />
                        {t("about.title")}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Version Info */}
                    <section>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">Junet</h3>
                                <p className="text-sm text-muted-foreground">
                                    {t("about.description")}
                                </p>
                            </div>
                            <Badge variant="outline" className="text-base px-3 py-1">
                                v{version}
                            </Badge>
                        </div>
                    </section>

                    <Separator />

                    {/* Features */}
                    <section>
                        <h3 className="mb-3 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                            {t("about.keyFeatures")}
                        </h3>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                <span className="text-sm">{t("about.features.ai")}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                <span className="text-sm">{t("about.features.privacy")}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                <span className="text-sm">{t("about.features.extensible")}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                <span className="text-sm">{t("about.features.multiAgent")}</span>
                            </div>
                        </div>
                    </section>

                    <Separator />

                    {/* Links */}
                    <section>
                        <h3 className="mb-3 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                            {t("about.links")}
                        </h3>
                        <div className="grid gap-2">
                            <Button
                                variant="outline"
                                className="w-full justify-between"
                                onClick={() => window.open("https://junet.io", "_blank")}
                            >
                                <span className="flex items-center gap-2">
                                    <Info className="h-4 w-4" />
                                    {t("about.website")}
                                </span>
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full justify-between"
                                onClick={() => window.open("https://junet.io/blog", "_blank")}
                            >
                                <span className="flex items-center gap-2">
                                    <ExternalLink className="h-4 w-4" />
                                    {t("about.blog")}
                                </span>
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full justify-between"
                                onClick={() => window.open("https://junet.io/changes", "_blank")}
                            >
                                <span className="flex items-center gap-2">
                                    <ExternalLink className="h-4 w-4" />
                                    {t("about.whatsNew")}
                                </span>
                            </Button>
                        </div>
                    </section>

                    <Separator />

                    {/* Made with love */}
                    <section>
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                            <span>{t("about.madeWith")}</span>
                            <Heart className="h-4 w-4 fill-red-500 text-red-500" />
                            <span>{t("about.byTeam")}</span>
                        </div>
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AboutDialog;
